import { CheckCircle2, Circle, Code2, Eye, Loader2, Network, RefreshCw, Rocket, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import type { PageProgress } from '@/features/_shared/channel-types'
import type { ChatProgressEvidence, PageProgressTitleCode } from '@/features/channel-router'
import { useStudioLocale } from '@/i18n'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { resumePersistedCandidateGenerationPreview } from '@/features/chat/services/candidate-generation-lifecycle'
import {
  clearStoredAppGenerationJob,
  isDirectAppGenerationLifecycleReservation,
  readStoredAppGenerationJob,
  resumeAppGenerationJobPolling,
  type AppGenerationBackgroundJobProgress,
} from '@/services/block-api-service'
import { useStudioStore } from '@/store/studio-store'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'
import { INK_100, INK_500, INK_700, FONT_MONO, MUTED_TEXT, OK, SOFT_BG, WARN } from './chat-tokens'
import { useGenerationNextActions } from './use-generation-next-actions'
import { CoogoMarkCount } from '@/components/coogo-count-badge'
import { settleBoundProjectChatGenerationOutcome } from '@/features/chat/project-chat-thread'
import type {
  CoogoMarkLayer,
  CoogoMarkPace,
  CoogoMarkState,
} from '@/components/coogo-status-mark'

interface ChatPageProgressProps {
  /** F9(c) — a STATE from the producer, never its words. */
  titleCode?: PageProgressTitleCode;
  pages: PageProgress[];
  elapsedMs: number;
  evidence?: ChatProgressEvidence;
  backgroundJob?: AppGenerationBackgroundJobProgress;
}

type LocalizedProgressCopy = (typeof COOGO_IDE_CHROME_COPY)[keyof typeof COOGO_IDE_CHROME_COPY]['generationProgress']

export interface CoogoProgressPresentation {
  state: CoogoMarkState
  layer: CoogoMarkLayer
  pace: CoogoMarkPace
}

export function deriveCoogoProgressPresentation({
  pages,
  evidence,
  backgroundJob,
  resumeError,
}: Pick<ChatPageProgressProps, 'pages' | 'evidence' | 'backgroundJob'> & {
  resumeError?: string | null
}): CoogoProgressPresentation {
  if (resumeError) return { state: 'unavailable', layer: 'service', pace: 'held' }
  if (backgroundJob?.status === 'paused' || backgroundJob?.stage === 'lifecycle-finalization-pending') {
    return { state: 'waiting_user', layer: 'service', pace: 'held' }
  }
  if (backgroundJob?.status === 'queued' || backgroundJob?.status === 'reconnecting') {
    return { state: 'receiving', layer: 'platform', pace: 'normal' }
  }

  const hasActivePage = pages.some((page) => page.status === 'in-progress')
  const hasPendingPage = pages.some((page) => page.status === 'pending')
  const isComplete = pages.length > 0 && pages.every((page) => page.status === 'done')
  const verificationPending = isComplete && Boolean(
    (evidence?.ontology?.status && evidence.ontology.status !== 'applied') ||
    (evidence?.actionFlow?.readinessStatus &&
      !['ready', 'not-required'].includes(evidence.actionFlow.readinessStatus)) ||
    (evidence?.publish?.readinessStatus && evidence.publish.readinessStatus !== 'ready'),
  )

  if (verificationPending) return { state: 'verifying', layer: 'service', pace: 'normal' }
  if (isComplete) return { state: 'complete', layer: 'overall', pace: 'held' }
  if (hasActivePage || backgroundJob?.status === 'generating') {
    return { state: 'acting', layer: 'agent', pace: 'deep' }
  }
  if (hasPendingPage) return { state: 'thinking', layer: 'agent', pace: 'deep' }
  return { state: 'receiving', layer: 'platform', pace: 'normal' }
}

/**
 * Multi-page generation progress — cogo-studio-specific extension of the
 * concept2 single-thinking-bubble pattern. Concept2 chat models a 1:1
 * conversation with a thinking state (3 dots); cogo-studio's Channel 2
 * generates N pages in parallel/sequence, so we keep the multi-line
 * checklist but conform to concept2 visuals: INK_100 bubble bg,
 * borderTopLeftRadius:4 tail, FONT_MONO for telemetry, Lucide icons for
 * status (no emoji per `feedback_no_emoji_icons.md`).
 */
export function ChatPageProgress({ titleCode, pages, elapsedMs, evidence, backgroundJob }: ChatPageProgressProps) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const progressCopy = copy.generationProgress
  // F9(c) — 'Generated app' used to arrive here as a producer LITERAL and
  // shadow `copy.generatedApp`, which already existed in all five languages.
  const title =
    titleCode === 'generated-app'
      ? copy.generatedApp
      : titleCode === 'generating-app'
        ? copy.generatingApp
        : titleCode === 'app-generation-failed'
          ? progressCopy.failedTitle
          : undefined
  const resolvedTitle = title ?? progressCopy.defaultTitle
  const { openPreview, openProjectOntology, openDeploy, openCode } = useGenerationNextActions()
  const developer = useStudioStore((s) => s.studioPersona === 'developer')
  const [isResuming, setIsResuming] = useState(false)
  const [resumeError, setResumeError] = useState<string | null>(null)
  const inProgressIdx = pages.findIndex((p) => p.status === 'in-progress')
  const doneCount = pages.filter((p) => p.status === 'done').length
  const isComplete = pages.length > 0 && doneCount === pages.length
  const lifecycleFinalizationPending =
    backgroundJob?.stage === 'lifecycle-finalization-pending'
  const totalProgress =
    (doneCount +
      (inProgressIdx >= 0 ? (pages[inProgressIdx].progress ?? 0) : 0)) /
    Math.max(pages.length, 1);

  const stageItems = buildProgressStages({ pages, evidence, isComplete, labels: progressCopy })
  const coogoProgress = deriveCoogoProgressPresentation({
    pages,
    evidence,
    backgroundJob,
    resumeError,
  })
  const coogoProgressLabel = resumeError
    ?? (coogoProgress.state === 'waiting_user'
      ? lifecycleFinalizationPending
        ? progressCopy.backgroundJob.finalizationPending
        : progressCopy.backgroundJob.paused
      : coogoProgress.state === 'complete'
        ? title ?? progressCopy.toneLabels.done
        : coogoProgress.state === 'verifying'
          ? title ?? progressCopy.toneLabels.review
          : resolvedTitle)

  const resumeBackgroundJob = async () => {
    if (!backgroundJob || isResuming) return
    setResumeError(null)
    setIsResuming(true)
    try {
      if (resumeAppGenerationJobPolling(backgroundJob.projectId)) return
      const descriptor = readStoredAppGenerationJob(backgroundJob.projectId)
      if (!descriptor) throw new Error(progressCopy.backgroundJob.resumeFailed)
      const directLifecycleReservation =
        lifecycleFinalizationPending &&
        isDirectAppGenerationLifecycleReservation(descriptor)
      if (
        descriptor.status !== 'lifecycle-pending' &&
        !directLifecycleReservation
      ) {
        const { registerStudioTools } = await import('@/mcp/register-studio-tools')
        const { resolveStudioToolRegistrationOptions } = await import(
          '@/mcp/studio-tool-registration-options'
        )
        registerStudioTools(resolveStudioToolRegistrationOptions())
        const result = await mcpBus.execute({
          tool: 'cogo.studio.requestAppGeneration',
          params: {
            appSpec: descriptor.appSpec,
            _expectedProjectId: descriptor.projectId,
            ...(descriptor.generationAuthorityAppSpecRef
              ? {
                  _generationAuthorityAppSpecRef:
                    descriptor.generationAuthorityAppSpecRef,
                }
              : {}),
            ...(descriptor.chatGeneratedApplicationDraftPreparation
              ? {
                  _chatGeneratedApplicationDraftPreparation:
                    descriptor.chatGeneratedApplicationDraftPreparation,
                }
              : {}),
            _appGenerationJobResume: {
              schemaVersion: 'studio-app-generation-job-resume.v1',
              requestId: descriptor.requestId,
              jobId: descriptor.jobId,
              appSpecFingerprint: descriptor.appSpecFingerprint,
            },
          },
        })
        if (!result.success) {
          throw new Error(result.error ?? progressCopy.backgroundJob.resumeFailed)
        }
      }
      await resumePersistedCandidateGenerationPreview(descriptor.projectId)
      const userId = useStudioStore.getState().session?.user?.id
      if (userId) {
        const settled = settleBoundProjectChatGenerationOutcome(
          userId,
          {
            projectId: descriptor.projectId,
            requestId: descriptor.requestId,
            ...(descriptor.jobId ? { jobId: descriptor.jobId } : {}),
          },
          {
            status: 'succeeded',
            text: copy.generatedApp,
            retryable: false,
            timestamp: Date.now(),
          },
        )
        const outcome = settled?.turns.find(
          (turn) =>
            turn.kind === 'outcome' &&
            turn.generation?.projectId === descriptor.projectId &&
            (!descriptor.requestId ||
              turn.generation.requestId === descriptor.requestId) &&
            (!descriptor.jobId || turn.generation.jobId === descriptor.jobId),
        )
        const studio = useStudioStore.getState()
        if (
          settled &&
          outcome?.kind === 'outcome' &&
          settled.userId === userId &&
          studio.session?.user?.id === settled.userId &&
          studio.currentProjectId === settled.projectId
        ) {
          useStudioStore.setState({
            chatMessages: studio.chatMessages.map((message) =>
              message.id === outcome.id
                ? { ...outcome, kind: 'project-chat-outcome' }
                : message,
            ),
          })
        }
      }
      clearStoredAppGenerationJob(
        descriptor.projectId,
        descriptor.jobId,
        descriptor.requestId,
      )
      useStudioStore.getState().replacePageProgress?.(null)
    } catch (error) {
      console.error('[ChatPageProgress] Failed to resume generation updates', error)
      setResumeError(
        lifecycleFinalizationPending
          ? progressCopy.backgroundJob.finalizeFailed
          : progressCopy.backgroundJob.resumeFailed,
      )
      const pendingDescriptor = backgroundJob
        ? readStoredAppGenerationJob(backgroundJob.projectId)
        : null
      if (pendingDescriptor) {
        const pageProgressFrame = useStudioStore.getState().pageProgressFrame
        const frame =
          pageProgressFrame?.kind === 'page-progress'
            ? pageProgressFrame
            : null
        useStudioStore.getState().replacePageProgress?.({
          kind: 'page-progress',
          id: frame?.id ?? 'app-generation-progress-frame',
          timestamp: frame?.timestamp ?? Date.now(),
          titleCode: frame?.titleCode,
          pages: frame?.pages ?? pages,
          elapsedMs: frame?.elapsedMs ?? elapsedMs,
          evidence: frame?.evidence,
          backgroundJob: {
            schemaVersion: 'studio-app-generation-background-progress.v1',
            projectId: pendingDescriptor.projectId,
            ...(pendingDescriptor.jobId
              ? { jobId: pendingDescriptor.jobId }
              : {}),
            status: 'paused',
            stage: 'lifecycle-finalization-pending',
            canResume: true,
            continuesInBackground: true,
            ...(pendingDescriptor.terminalSloMs
              ? { terminalSloMs: pendingDescriptor.terminalSloMs }
              : {}),
          },
        })
      }
    } finally {
      setIsResuming(false)
    }
  }

  return (
    <div
      className="flex justify-start"
      data-testid="chatrail-streaming-progress"
      aria-live="polite"
    >
      <div
        style={{
          maxWidth: "85%",
          padding: "10px 14px",
          background: INK_100,
          color: INK_700,
          borderRadius: 14,
          borderTopLeftRadius: 4,
          fontSize: 13.5,
          lineHeight: 1.5,
          minWidth: 220,
        }}
      >
        <div
          className="flex min-w-0 items-center justify-between gap-3"
          style={{
            fontSize: 11,
            color: MUTED_TEXT,
            marginBottom: 8,
          }}
        >
          <CoogoMarkCount
            className="min-w-0 max-w-[15rem]"
            state={coogoProgress.state}
            layer={coogoProgress.layer}
            pace={coogoProgress.pace}
            count={pages.length}
            unit={progressCopy.pageUnit(pages.length)}
            size="micro"
            markSize="inline-compact"
            statusLabel={coogoProgressLabel}
            accessibleLabel={progressCopy.countAnnouncement(
              coogoProgressLabel,
              pages.length,
            )}
          />
          <span className="shrink-0" style={{ fontFamily: FONT_MONO }}>
            {Math.round(elapsedMs / 1000)}s
          </span>
        </div>
        <ProgressStageStrip
          stages={stageItems}
          ariaLabel={progressCopy.ariaLabel}
          toneLabels={progressCopy.toneLabels}
        />
        <ul className="flex flex-col gap-1">
          {pages.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-1.5"
              style={{ fontSize: 12 }}
            >
              {p.status === "done" && (
                <CheckCircle2
                  size={13}
                  aria-hidden
                  style={{ color: OK, flexShrink: 0 }}
                />
              )}
              {p.status === "in-progress" && (
                <Loader2
                  size={13}
                  aria-hidden
                  className="animate-spin"
                  style={{ color: INK_500, flexShrink: 0 }}
                />
              )}
              {p.status === "pending" && (
                <Circle
                  size={13}
                  aria-hidden
                  style={{ color: MUTED_TEXT, opacity: 0.6, flexShrink: 0 }}
                />
              )}
              <span
                className="studio-ellipsis-flex"
                title={p.name}
                style={{ color: p.status === "pending" ? MUTED_TEXT : INK_700 }}
              >
                {p.name}
              </span>
              {p.status === "in-progress" && p.progress !== undefined && (
                <span
                  className="ml-auto"
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 11,
                    color: MUTED_TEXT,
                  }}
                >
                  {Math.round(p.progress * 100)}%
                </span>
              )}
            </li>
          ))}
        </ul>
        <div
          className="mt-2.5 overflow-hidden rounded-full"
          style={{ height: 4, background: SOFT_BG }}
        >
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${totalProgress * 100}%`,
              background: "hsl(var(--foreground) / 0.42)",
            }}
          />
        </div>
        {backgroundJob && (!isComplete || backgroundJob.canResume || resumeError) && (
          <div
            className="mt-2.5 rounded-md border border-border bg-background/75 px-2.5 py-2"
            data-testid="chatrail-generation-background-job"
            role="status"
          >
            <div className="flex min-w-0 items-start gap-2">
              <RefreshCw
                className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                  backgroundJob.status === 'reconnecting' || isResuming
                    ? 'animate-spin'
                    : ''
                }`}
                aria-hidden
                style={{ color: INK_500 }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium text-foreground">
                  {lifecycleFinalizationPending
                    ? progressCopy.backgroundJob.finalizationPending
                    : progressCopy.backgroundJob[backgroundJob.status]}
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {lifecycleFinalizationPending
                    ? progressCopy.backgroundJob.finalizationDetail
                    : progressCopy.backgroundJob.continues}
                </p>
              </div>
            </div>
            {backgroundJob.canResume && (
              <button
                type="button"
                className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-60"
                data-testid="chatrail-generation-resume"
                onClick={() => void resumeBackgroundJob()}
                disabled={isResuming}
                aria-label={
                  lifecycleFinalizationPending
                    ? progressCopy.backgroundJob.finalize
                    : progressCopy.backgroundJob.resume
                }
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isResuming ? 'animate-spin' : ''}`}
                  aria-hidden
                />
                {isResuming
                  ? progressCopy.backgroundJob.resuming
                  : lifecycleFinalizationPending
                    ? progressCopy.backgroundJob.finalize
                    : progressCopy.backgroundJob.resume}
              </button>
            )}
            {resumeError && (
              <p className="mt-1.5 text-[11px] text-destructive" role="alert">
                {resumeError}
              </p>
            )}
          </div>
        )}
        {developer && evidence && <ProgressEvidenceStrip evidence={evidence} copy={progressCopy} />}
        {isComplete && (
          <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="chatrail-generation-next-actions">
            <ProgressActionButton
              icon={Eye}
              label={copy.surfaceLabels.preview}
              testId="chatrail-action-open-preview"
              onClick={openPreview}
            />
            <ProgressActionButton
              icon={Network}
              label={copy.surfaceLabels.library}
              testId="chatrail-action-open-library"
              onClick={openProjectOntology}
            />
            <ProgressActionButton
              icon={Rocket}
              label={copy.surfaceLabels.deploy}
              testId="chatrail-action-open-deploy"
              onClick={openDeploy}
            />
            {developer && (
              <ProgressActionButton
                icon={Code2}
                label={copy.surfaceLabels.code}
                testId="chatrail-action-open-code"
                onClick={openCode}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type ProgressStageTone = "done" | "active" | "pending" | "review";

interface ProgressStageItem {
  id: string;
  label: string;
  tone: ProgressStageTone;
}

interface ProgressStageLabels {
  request: string
  source: string
  build: string
  logic: string
  app: string
  launch: string
}

function buildProgressStages({
  pages,
  evidence,
  isComplete,
  labels,
}: {
  pages: PageProgress[]
  evidence?: ChatProgressEvidence
  isComplete: boolean
  labels: ProgressStageLabels
}): ProgressStageItem[] {
  const ontologyStatus = evidence?.ontology?.status;
  const ontologyClaimLevel = evidence?.ontology?.claimLevel;
  const actionFlowStatus = evidence?.actionFlow?.readinessStatus;
  const publishStatus = evidence?.publish?.readinessStatus;
  const hasActivePage = pages.some((page) => page.status === "in-progress");
  const hasAnyPage = pages.length > 0;
  const ontologyApplied =
    ontologyStatus === "applied" || ontologyClaimLevel === "applied";
  const actionFlowReady =
    actionFlowStatus === "ready" ||
    actionFlowStatus === "not-required" ||
    (evidence?.actionFlow?.compilerValid === true &&
      evidence?.actionFlow?.runtimeValid === true);
  const publishReady =
    evidence?.publish?.publishReady === true || publishStatus === "ready";

  return [
    {
      id: 'understand',
      label: labels.request,
      tone: hasAnyPage ? 'done' : 'active',
    },
    {
      id: 'source-rules',
      label: labels.source,
      tone: ontologyApplied ? 'done' : ontologyStatus ? 'review' : hasAnyPage ? 'active' : 'pending',
    },
    {
      id: 'draft',
      label: labels.build,
      tone: isComplete ? 'done' : hasActivePage || hasAnyPage ? 'active' : 'pending',
    },
    {
      id: 'logic',
      label: labels.logic,
      tone: actionFlowReady ? 'done' : actionFlowStatus ? 'review' : isComplete ? 'active' : 'pending',
    },
    { id: 'app', label: labels.app, tone: isComplete ? 'done' : 'pending' },
    {
      id: 'publish',
      label: labels.launch,
      tone: publishReady ? 'done' : publishStatus ? 'review' : isComplete ? 'active' : 'pending',
    },
  ];
}

function ProgressStageStrip({
  stages,
  ariaLabel,
  toneLabels,
}: {
  stages: ProgressStageItem[]
  ariaLabel: string
  toneLabels: LocalizedProgressCopy['toneLabels']
}) {
  return (
    <div
      className="mb-2 flex min-w-0 flex-wrap gap-1"
      data-testid="chatrail-generation-stage-strip"
      aria-label={ariaLabel}
    >
      {stages.map((stage) => (
        <span
          key={stage.id}
          className="inline-flex h-5 max-w-full items-center gap-1 rounded border px-1.5 text-[10px] font-medium"
          style={{
            borderColor: stageBorder(stage.tone),
            background: stageBackground(stage.tone),
            color: stageTextColor(stage.tone),
          }}
          data-stage-tone={stage.tone}
          title={`${stage.label}: ${toneLabels[stage.tone]}`}
        >
          {stage.tone === "done" && (
            <CheckCircle2 className="h-2.5 w-2.5 shrink-0" aria-hidden />
          )}
          {stage.tone === "active" && (
            <Loader2
              className="h-2.5 w-2.5 shrink-0 animate-spin"
              aria-hidden
            />
          )}
          {stage.tone === "review" && (
            <span
              aria-hidden
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: WARN }}
            />
          )}
          {stage.tone === "pending" && (
            <Circle className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden />
          )}
          <span className="truncate">{stage.label}</span>
        </span>
      ))}
    </div>
  );
}

function stageBorder(tone: ProgressStageTone): string {
  if (tone === "done") return "hsl(var(--border))";
  if (tone === "review") return "hsl(var(--border))";
  if (tone === "active") return "hsl(var(--foreground) / 0.18)";
  return "hsl(var(--border))";
}

function stageBackground(tone: ProgressStageTone): string {
  if (tone === "done") return "hsl(var(--background) / 0.76)";
  if (tone === "review") return "hsl(var(--background) / 0.76)";
  if (tone === "active") return "hsl(var(--foreground) / 0.06)";
  return "hsl(var(--background) / 0.64)";
}

function stageTextColor(tone: ProgressStageTone): string {
  if (tone === "pending") return MUTED_TEXT;
  return INK_700;
}

function ProgressEvidenceStrip({ evidence, copy }: { evidence: ChatProgressEvidence; copy: LocalizedProgressCopy }) {
  const ontologyStatus =
    evidence.ontology?.status ?? (evidence.ontology?.utilizationScore !== undefined ? 'matched' : undefined)
  const scenarioIds = evidence.ontology?.selectedScenarioIds?.slice(0, 2) ?? []
  const primitiveCount = evidence.ontology?.primitiveCount
  const utilizationScore = evidence.ontology?.utilizationScore
  const conformanceStatus = evidence.ontology?.conformanceStatus
  const visibleFromJsonStatus = evidence.ontology?.visibleFromJsonStatus
  const designQualityStatus = evidence.ontology?.designQualityStatus
  const ontologyDisplayStatus = localizeEvidenceStatus(formatOntologyStatus(evidence.ontology, ontologyStatus), copy)
  const ontologyBlockedReason = formatOntologyBlockedReason(evidence.ontology)
  const actionFlowStatus =
    evidence.actionFlow?.readinessStatus ??
    (evidence.actionFlow?.compilerValid === true &&
    evidence.actionFlow?.runtimeValid === true
      ? "ready"
      : undefined);
  const publishStatus =
    evidence.publish?.readinessStatus ?? (evidence.publish?.publishReady === true ? 'ready' : undefined)
  const speedLabel = formatSpeedEvidence(evidence.speed, copy)

  return (
    <div
      className="mt-2.5 rounded-md border border-border/70 bg-background/70 px-2.5 py-2"
      data-testid="chatrail-ontology-progress-evidence"
    >
      <div className="grid gap-1.5 text-[11px]" style={{ color: INK_700 }}>
        {ontologyStatus && (
          <EvidenceRow
            label={copy.evidence.source}
            value={[
              ontologyDisplayStatus,
              scenarioIds.length > 0 ? scenarioIds.join(', ') : undefined,
              primitiveCount !== undefined ? copy.evidence.primitiveCount(primitiveCount) : undefined,
              conformanceStatus
                ? `${copy.evidence.conformance} ${localizeEvidenceStatus(conformanceStatus, copy)}`
                : undefined,
              visibleFromJsonStatus
                ? `${copy.evidence.visible} ${localizeEvidenceStatus(visibleFromJsonStatus, copy)}`
                : undefined,
              designQualityStatus
                ? `${copy.evidence.design} ${localizeEvidenceStatus(designQualityStatus, copy)}`
                : undefined,
              utilizationScore !== undefined && evidence.ontology?.claimLevel === 'applied'
                ? `${utilizationScore}%`
                : undefined,
              ontologyBlockedReason,
            ]
              .filter(Boolean)
              .join(" · ")}
            tone={
              evidence.ontology?.claimLevel === "applied" ||
              ontologyStatus === "applied"
                ? "success"
                : "warning"
            }
          />
        )}
        {actionFlowStatus && (
          <EvidenceRow
            label={copy.evidence.logic}
            value={[
              localizeEvidenceStatus(actionFlowStatus, copy),
              evidence.actionFlow?.compilerValid === true ? copy.evidence.compilerValid : undefined,
              evidence.actionFlow?.runtimeValid === true ? copy.evidence.runtimeValid : undefined,
            ]
              .filter(Boolean)
              .join(" · ")}
            tone={actionFlowStatus === "ready" ? "success" : "warning"}
          />
        )}
        {speedLabel && (
          <EvidenceRow
            label={copy.evidence.speed}
            value={speedLabel}
            tone={evidence.speed?.class === "slow" ? "warning" : "success"}
          />
        )}
        {publishStatus && (
          <EvidenceRow
            label={copy.evidence.launch}
            value={[
              localizeEvidenceStatus(publishStatus, copy),
              evidence.publish?.publishReady === true ? localizeEvidenceStatus('ready', copy) : undefined,
            ]
              .filter(Boolean)
              .join(" · ")}
            tone={publishStatus === "ready" ? "success" : "warning"}
          />
        )}
      </div>
    </div>
  );
}

function formatOntologyBlockedReason(
  ontology: ChatProgressEvidence["ontology"],
): string | undefined {
  const reason = ontology?.blockedReason;
  if (!reason) return undefined;
  const duplicateReasons = [
    ontology?.conformanceStatus
      ? `conformance ${ontology.conformanceStatus}`
      : undefined,
    ontology?.visibleFromJsonStatus
      ? `visible ${ontology.visibleFromJsonStatus}`
      : undefined,
    ontology?.designQualityStatus
      ? `design ${ontology.designQualityStatus}`
      : undefined,
  ];
  return duplicateReasons.includes(reason) ? undefined : reason;
}

function formatOntologyStatus(
  ontology: ChatProgressEvidence["ontology"],
  fallbackStatus?: string,
): string {
  if (ontology?.claimLevel === "applied" || fallbackStatus === "applied")
    return "applied";
  if (ontology?.claimLevel === "missing" || fallbackStatus === "missing")
    return "missing";
  if (ontology?.claimLevel === "candidate" || fallbackStatus === "candidate")
    return "candidate";
  if (fallbackStatus === "matched" || fallbackStatus === "partial")
    return "candidate";
  return fallbackStatus ?? "missing";
}

function localizeEvidenceStatus(status: string | undefined, copy: LocalizedProgressCopy): string | undefined {
  if (!status) return undefined
  if (status in copy.statusLabels) {
    return copy.statusLabels[status as keyof typeof copy.statusLabels]
  }
  return status
}

function EvidenceRow({ label, value, tone }: { label: string; value: string; tone: 'success' | 'warning' }) {
  return (
    <div className="grid min-w-0 grid-cols-[64px_minmax(0,1fr)] items-start gap-2">
      <span className="flex items-center gap-1.5" style={{ color: MUTED_TEXT }}>
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ background: tone === "success" ? OK : WARN }}
        />
        {label}
      </span>
      <span
        className="studio-ellipsis-block font-mono"
        title={value}
        style={{ fontFamily: FONT_MONO }}
      >
        {value}
      </span>
    </div>
  );
}

function formatSpeedEvidence(speed: ChatProgressEvidence['speed'], copy: LocalizedProgressCopy): string | null {
  if (!speed) return null
  const parts = [
    localizeEvidenceStatus(speed.class, copy),
    speed.latencyMs !== undefined ? formatDuration(speed.latencyMs) : undefined,
    speed.targetMs !== undefined ? `${copy.evidence.target} ${formatDuration(speed.targetMs)}` : undefined,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${Math.round(ms / 100) / 10}s`;
}

function ProgressActionButton({
  icon: Icon,
  label,
  testId,
  onClick,
  variant = "secondary",
}: {
  icon: LucideIcon;
  label: string;
  testId: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
}) {
  const className =
    variant === "primary"
      ? "inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border bg-foreground px-2.5 py-1.5 text-[11px] font-semibold text-background transition-colors hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      : "inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border bg-background/80 px-2.5 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={className}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span>{label}</span>
    </button>
  );
}
